<template>
  <!-- Звонок на общий номер WhatsApp Business (DEV-2418). Висит в углу экрана на
       любой странице CRM: входящее видят все менеджеры, забирает первый ответивший. -->
  <Transition name="waba-call-fade">
    <div
      v-if="visible"
      class="waba-call"
      :class="{ 'waba-call--incoming': callState === 'incoming' }"
      role="dialog"
      :aria-label="title"
    >
      <div v-if="showEnded && callState === 'ended'" class="waba-call__ended" role="status">
        <CheckIcon :size="18" aria-hidden="true" />
        <span>{{ $t('messenger.telegram.call.endedTitle') }} · {{ formattedDuration }}</span>
      </div>

      <template v-else>
        <div class="waba-call__head">
          <div class="waba-call__avatar" aria-hidden="true">
            <PhoneIcon v-if="nameIsPhone" :size="22" />
            <template v-else>{{ initials }}</template>
          </div>
          <div class="waba-call__who">
            <span class="waba-call__title">{{ title }}</span>
            <span class="waba-call__name">{{ displayName }}</span>
            <span v-if="!nameIsPhone" class="waba-call__phone">{{ phoneLabel }}</span>
            <RouterLink v-if="clientLink" :to="clientLink" class="waba-call__client">
              {{ $t('messenger.whatsapp.call.openClient') }}
            </RouterLink>
          </div>
          <!-- DEV-2502. Убрать карточку у себя, не отклоняя звонок за всех: висела до 75 с. -->
          <button
            v-if="callState === 'incoming'"
            type="button"
            class="waba-call__hide"
            :title="$t('messenger.whatsapp.call.hideForMe')"
            :aria-label="$t('messenger.whatsapp.call.hideForMe')"
            @click="hideIncoming"
          >
            <XIcon :size="18" />
          </button>
        </div>

        <div v-if="isActive" class="waba-call__status" aria-live="polite">
          <span v-if="callState === 'active'" class="waba-call__timer">{{
            formattedDuration
          }}</span>
          <span v-else-if="callState === 'connecting'">
            {{ $t('messenger.telegram.call.state.connecting') }}
          </span>
        </div>

        <div v-if="callState === 'incoming'" class="waba-call__actions">
          <button
            type="button"
            class="waba-call__btn waba-call__btn--decline"
            @click="declineIncoming"
          >
            <XIcon :size="20" />
            <span>{{ $t('messenger.telegram.call.decline') }}</span>
          </button>
          <button
            type="button"
            class="waba-call__btn waba-call__btn--accept"
            @click="acceptIncoming"
          >
            <PhoneIcon :size="20" />
            <span>{{ $t('messenger.telegram.call.accept') }}</span>
          </button>
        </div>
        <!-- DEV-2568. Без разрешения системного уведомления звонок в фоне не увидят. -->
        <button
          v-if="callState === 'incoming' && permission === 'default'"
          type="button"
          class="waba-call__notify"
          @click="askPermission"
        >
          {{ $t('messenger.callEvent.enableNotifications') }}
        </button>
        <!-- На тач-экране подсказки в title не видно, а отказ касается всех. -->
        <p v-if="callState === 'incoming'" class="waba-call__hint">
          {{ $t('messenger.whatsapp.call.declineHint') }}
        </p>

        <div v-else-if="callState === 'ended'" class="waba-call__actions">
          <button type="button" class="waba-call__btn waba-call__btn--mute" @click="closeCall">
            <XIcon :size="20" />
            <span>{{ $t('messenger.telegram.call.close') }}</span>
          </button>
        </div>

        <div v-else class="waba-call__actions">
          <button
            type="button"
            class="waba-call__btn waba-call__btn--mute"
            :class="{ 'waba-call__btn--mute-on': isMuted }"
            :aria-pressed="isMuted"
            :disabled="callState !== 'active'"
            @click="toggleMute"
          >
            <MicOffIcon v-if="isMuted" :size="20" />
            <MicIcon v-else :size="20" />
            <span>{{ $t('messenger.telegram.call.mute') }}</span>
          </button>
          <button type="button" class="waba-call__btn waba-call__btn--decline" @click="hangup">
            <PhoneOffIcon :size="20" />
            <span>{{ $t('messenger.telegram.call.hangup') }}</span>
          </button>
        </div>

        <p v-if="errorLabel" class="waba-call__error">{{ errorLabel }}</p>

        <!-- DEV-2484: кто ещё звонит, пока окно занято. Принять — после этого звонка. -->
        <div v-if="waitingCalls.length" class="waba-call__waiting" aria-live="polite">
          <span class="waba-call__waiting-title">{{ $t('messenger.whatsapp.call.waiting') }}</span>
          <ul class="waba-call__waiting-list">
            <li v-for="w in waitingCalls" :key="w.call_id" class="waba-call__waiting-row">
              <span class="waba-call__waiting-name">{{
                callerLabel(w.peer_name, w.peer_phone)
              }}</span>
              <button
                type="button"
                class="waba-call__waiting-decline"
                :aria-label="`${$t('messenger.telegram.call.decline')}: ${callerLabel(w.peer_name, w.peer_phone)}`"
                @click="declineWaiting(w.call_id)"
              >
                {{ $t('messenger.telegram.call.decline') }}
              </button>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { CheckIcon, MicIcon, MicOffIcon, PhoneIcon, PhoneOffIcon, XIcon } from 'lucide-vue-next'
import { useWabaCall } from '@/features/messenger/composables/useWabaCall'
import { useIncomingCallAlert } from '@/features/messenger/composables/useIncomingCallAlert'

const { t } = useI18n()

const {
  callState,
  callId,
  peerName,
  peerPhone,
  clientId,
  isMuted,
  isActive,
  waitingCalls,
  callError,
  formattedDuration,
  acceptIncoming,
  declineIncoming,
  hideIncoming,
  declineWaiting,
  hangup,
  toggleMute,
  closeCall,
  resetAll,
  setSyncEnabled,
  syncRinging,
} = useWabaCall()

// DEV-2570. Окно звонка смонтировано только у тех, кто может отвечать: эта вкладка
// спрашивает звонящие при подключении сокета — и сразу, если сокет уже открыт.
onMounted(() => {
  setSyncEnabled(true)
  syncRinging()
})

// Состоявшийся разговор гаснет через короткое «Звонок завершён»; оборванный с
// ошибкой — остаётся с текстом ошибки, пока его не закроют «Завершить».
const showEnded = ref(false)
let endedTimer: ReturnType<typeof setTimeout> | null = null

watch(callState, (next, prev) => {
  if (next !== 'ended') {
    showEnded.value = false
    return
  }
  if (prev === 'active' && !callError.value) {
    showEnded.value = true
    if (endedTimer) clearTimeout(endedTimer)
    endedTimer = setTimeout(() => {
      showEnded.value = false
      // За 1,5 с могло прийти новое входящее — его не закрываем.
      if (callState.value === 'ended') closeCall()
    }, 1500)
  } else if (!callError.value) {
    closeCall()
  }
})

// Выход из CRM во время звонка: без окна микрофон и соединение остались бы жить.
onUnmounted(() => {
  if (endedTimer) clearTimeout(endedTimer)
  setSyncEnabled(false)
  resetAll()
})

const visible = computed(
  () =>
    callState.value !== 'idle' &&
    (callState.value !== 'ended' || showEnded.value || !!callError.value),
)

const title = computed(() => t('messenger.whatsapp.call.incoming'))
const phoneLabel = computed(() => (peerPhone.value ? `+${peerPhone.value}` : ''))
// У незнакомого номера сервер кладёт в имя сам номер — второй раз его не пишем.
const isPhone = (name: string | undefined, phone: string) =>
  !name || name.replace(/\D/g, '') === phone
const nameIsPhone = computed(() => isPhone(peerName.value, peerPhone.value))

function callerLabel(name: string | undefined, phone: string) {
  if (name && !isPhone(name, phone)) return name
  return phone ? `+${phone}` : t('messenger.telegram.call.unknownPeer')
}

const displayName = computed(() => callerLabel(peerName.value, peerPhone.value))

// DEV-2568. Звонящий слышен и виден вне вкладки: мелодия, уведомление, заголовок.
const { permission, askPermission } = useIncomingCallAlert(
  computed(() =>
    callState.value === 'incoming' && callId.value
      ? { id: callId.value, title: title.value, body: displayName.value }
      : null,
  ),
)

const initials = computed(() =>
  displayName.value
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join(''),
)

// Карточка клиента сразу на вкладке WhatsApp — там история звонков.
const clientLink = computed(() =>
  clientId.value
    ? {
        name: 'clients-detail',
        params: { id: clientId.value },
        query: { tab: 'chats', provider: 'whatsapp' },
      }
    : null,
)

const errorLabel = computed(() => {
  if (!callError.value) return ''
  const key = `messenger.telegram.call.error.${callError.value}`
  const label = t(key)
  return label === key ? t('messenger.telegram.call.error.generic') : label
})
</script>

<style scoped>
.waba-call {
  position: fixed;
  /* Сверху справа: внизу справа живёт виджет SIP-звонка (ActiveCallWidget). */
  top: 76px;
  right: 24px;
  /* Выше модалки (9999) и поповеров (10000): на звонок отвечают из любого окна. */
  z-index: 10001;
  width: min(320px, calc(100vw - 32px));
  padding: 16px;
  background: var(--color-background-primary, #fff);
  border-radius: 16px;
  box-shadow:
    0 12px 40px rgba(15, 33, 51, 0.22),
    0 2px 8px rgba(15, 33, 51, 0.1);
}

.waba-call__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

/* Пока звонят — выше и тостов (DEV-2502): ниже 641px они сверху и ложились на «Принять».
   Звонят до 75 с, карточку можно скрыть крестиком. В разговоре тосты снова над ней —
   «Номер скопирован» и ошибки сохранения не теряются. */
.waba-call--incoming {
  z-index: calc(var(--z-index-toast, 10010) + 1);
}

/* Крестик в углу — 44×44, но поля карточки не раздвигает. */
.waba-call__hide {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -12px -12px 0 auto;
  padding: 0;
  border: none;
  border-radius: var(--radius-full, 999px);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}

.waba-call__hide:hover {
  color: var(--color-text-primary);
}

.waba-call__avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #25d366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.waba-call__who {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.waba-call__title {
  font-size: 12px;
  font-weight: 600;
  color: #128c7e;
}

.waba-call__name {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-primary, #18181b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waba-call__phone {
  font-size: 13px;
  color: var(--color-text-secondary, #52525b);
  font-variant-numeric: tabular-nums;
}

.waba-call__client {
  /* Глобальные a { padding; a:hover зелёный фон } из main.css здесь не нужны. */
  padding: 0;
  background-color: transparent;
  font-size: 13px;
  color: var(--color-primary, #2563eb);
}

.waba-call__status {
  min-height: 20px;
  margin: 8px 0 0;
  font-size: 14px;
  color: #128c7e;
}

.waba-call__timer {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.waba-call__actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.waba-call__btn {
  flex: 1;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
}

.waba-call__btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.waba-call__btn:focus-visible {
  outline: 3px solid rgba(37, 211, 102, 0.4);
  outline-offset: 2px;
}

.waba-call__btn--accept {
  background: #22c55e;
}

.waba-call__btn--decline {
  background: #ef4444;
}

.waba-call__btn--mute {
  background: #6b7280;
}

.waba-call__btn--mute-on {
  background: #f59e0b;
  color: #18181b;
}

.waba-call__hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--color-text-secondary, #52525b);
}

.waba-call__notify {
  margin: 10px 0 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  color: var(--color-primary, #2563eb);
  text-align: left;
  cursor: pointer;
}

.waba-call__notify:hover {
  text-decoration: underline;
}

.waba-call__error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #ef4444;
}

.waba-call__waiting {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border-light, #e4e4e7);
}

.waba-call__waiting-title {
  display: block;
  font-size: 12px;
  line-height: 1.4;
  color: var(--color-text-secondary, #52525b);
}

.waba-call__waiting-list {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.waba-call__waiting-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.waba-call__waiting-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #18181b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waba-call__waiting-decline {
  flex-shrink: 0;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #ef4444;
  cursor: pointer;
}

.waba-call__waiting-decline:focus-visible {
  outline: 3px solid rgba(239, 68, 68, 0.35);
  outline-offset: 2px;
}

.waba-call__ended {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #18181b);
  font-variant-numeric: tabular-nums;
}

.waba-call-fade-enter-active,
.waba-call-fade-leave-active {
  transition: opacity 0.18s ease;
}

.waba-call-fade-enter-from,
.waba-call-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .waba-call {
    top: 68px;
    right: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .waba-call-fade-enter-active,
  .waba-call-fade-leave-active {
    transition: none;
  }
}
</style>
